import { useState, useEffect } from 'react';
import {
  MeDto,
  AppSettingsContentResponseModel,
  CountryContentResponseModel,
  PersonalInformationScreenPropertiesModel,
  UpdateProfileRequest,
} from '@/services/platform/api/model';
import { getProfileCountries } from '@/services/platform/data/profile';
import { Drawer, DrawerClose, DrawerContent, DrawerFooter } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Phone } from '@/components/ui/phone';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useClientTranslation } from '@/lib/core';
import { useLocaleStore } from '@/lib/core/stores';

const DEFAULT_COUNTRY_CODE = '+351';

function parsePhone(full: string | null | undefined): { countryCode: string; number: string } {
  if (!full) return { countryCode: DEFAULT_COUNTRY_CODE, number: '' };
  if (full.startsWith('+')) {
    const spaceIdx = full.indexOf(' ');
    if (spaceIdx > 0) return { countryCode: full.slice(0, spaceIdx), number: full.slice(spaceIdx + 1) };
  }
  return { countryCode: DEFAULT_COUNTRY_CODE, number: full };
}

export interface EditContactSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: MeDto | undefined;
  pageProperties: PersonalInformationScreenPropertiesModel | undefined;
  appSettings: AppSettingsContentResponseModel | undefined;
  onSave: (data: UpdateProfileRequest) => Promise<void>;
}

export function EditContactSheet({
  open,
  onOpenChange,
  profile,
  pageProperties,
  appSettings,
  onSave,
}: EditContactSheetProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);
  const [saving, setSaving] = useState(false);
  const [countries, setCountries] = useState<CountryContentResponseModel[]>([]);

  const parsed = parsePhone(profile?.otherPhone ?? profile?.phone);
  const [countryCode, setCountryCode] = useState(parsed.countryCode);
  const [phoneNumber, setPhoneNumber] = useState(parsed.number);
  const [country, setCountry] = useState(
    profile?.countryOfResidenceName ?? profile?.billingCountry ?? profile?.nationality ?? ''
  );
  const [countryISOCode, setCountryISOCode] = useState(profile?.countryOfResidenceISO);

  useEffect(() => {
    if (!open) return;
    const p = parsePhone(profile?.otherPhone ?? profile?.phone);
    setCountryCode(p.countryCode);
    setPhoneNumber(p.number);
    setCountry(profile?.countryOfResidenceName ?? profile?.billingCountry ?? profile?.nationality ?? '');
    setCountryISOCode(profile?.countryOfResidenceISO);
  }, [open, profile]);

  useEffect(() => {
    if (!open || !appSettings) return;
    getProfileCountries(appSettings, locale).then(items => {
      if (items) setCountries(items);
    });
  }, [open, appSettings, locale]);

  async function handleSave() {
    setSaving(true);
    try {
      const fullPhone = phoneNumber ? `${countryCode} ${phoneNumber}` : '';
      await onSave({
        otherPhone: fullPhone || null,
        countryOfResidenceName: country,
        countryOfResidenceISO: countryISOCode,
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        {/* Header */}
        <div className="flex items-center justify-between px-2 py-4">
          <div className="size-11" />
          <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px] uppercase">
            {pageProperties?.editContactInformationTitle ?? t('App.Labels.EditContactInformation')}
          </p>
          <div className="size-11" />
        </div>

        {/* Form */}
        <div className="flex flex-col gap-4 px-4 py-6">
          <div className="flex flex-col gap-1">
            <Label htmlFor="edit-phone" className="font-inter">
              {t('App.Labels.OtherPhoneNumber')}
            </Label>
            <Phone
              id="edit-phone"
              appSettings={appSettings?.properties}
              countryCode={countryCode}
              value={phoneNumber}
              withoutPortal
              onCountryCodeChange={setCountryCode}
              onChange={setPhoneNumber}
            />
          </div>

          {/* Countries */}
          <div className="flex flex-col gap-1">
            <Label htmlFor="edit-country" className="font-inter">
              {t('App.labels.CountryOfResidence')}
            </Label>
            <Select
              value={country}
              onValueChange={value => {
                setCountry(value);
                const selected = countries.find(
                  item => (item.properties?.countryName ?? item.name ?? '') === value
                );
                setCountryISOCode(selected?.properties?.countryCode);
              }}
            >
              <SelectTrigger id="edit-country" className="font-normal normal-case">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent withoutPortal>
                {countries.map(item => {
                  const label = item.properties?.countryName ?? item.name ?? '';
                  return (
                    <SelectItem key={item.id} value={label}>
                      {label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Footer */}
        <DrawerFooter>
          <Button variant="primary-solid" palette="dark" size="md" loading={saving} onClick={handleSave}>
            {pageProperties?.EditContactInformationSaveChangesBtn ?? t('App.Labels.SaveChanges')}
          </Button>
          <DrawerClose
            render={
              <Button variant="tertiary-link" palette="dark" size="sm">
                {pageProperties?.editContactInformationCancelBtn ?? t('App.Labels.Cancel')}
              </Button>
            }
          />
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
